import React, { useEffect, useRef, useState } from 'react';
import { useAnimatedNavigate } from '../../../hooks/useAnimatedNavigate';
import { PlusIcon, MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useBillingStore } from '../store/billingStore';
import { BILLING_STATUS_LABELS } from '../types/billing.types';
import type { BillingTab, BillingStatus } from '../types/billing.types';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../../components/ui/dropdown-menu';

interface BillingControlsBarProps {
  activeTab: BillingTab;
  onRefresh: () => void;
}

const BILLING_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'unbilled', label: BILLING_STATUS_LABELS.unbilled },
  { value: 'ready_to_bill', label: BILLING_STATUS_LABELS.ready_to_bill },
  { value: 'on_hold', label: BILLING_STATUS_LABELS.on_hold },
  { value: 'billed', label: BILLING_STATUS_LABELS.billed },
  { value: 'written_off', label: BILLING_STATUS_LABELS.written_off },
];

const BillingControlsBar: React.FC<BillingControlsBarProps> = ({ activeTab, onRefresh }) => {
  const navigate = useAnimatedNavigate();
  const store = useBillingStore();
  const [searchInput, setSearchInput] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentFilters =
    activeTab === 'time-entries'
      ? store.timeEntryFilters
      : activeTab === 'expenses'
        ? store.expenseFilters
        : store.billFilters;
  const setFilters =
    activeTab === 'time-entries'
      ? store.setTimeEntryFilters
      : activeTab === 'expenses'
        ? store.setExpenseFilters
        : store.setBillFilters;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilters({ search: searchInput || undefined });
      onRefresh();
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  const handleDateFrom = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ date_from: e.target.value || undefined });
    onRefresh();
  };

  const handleDateTo = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters({ date_to: e.target.value || undefined });
    onRefresh();
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (activeTab === 'invoices') {
      setFilters({ status: val === 'all' ? undefined : val } as any);
    } else {
      setFilters({
        billing_status: val === 'all' ? undefined : val as BillingStatus,
        is_billed: undefined,
      } as any);
    }
    onRefresh();
  };

  const handleCreate = () => {
    const drawerType = activeTab === 'time-entries' ? 'time-entry' : activeTab === 'expenses' ? 'expense' : 'bill';
    store.openDrawer(drawerType, 'create');
  };

  const statusValue =
    activeTab === 'invoices'
      ? ((currentFilters as any).status || 'all')
      : ((currentFilters as any).billing_status || 'all');

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      {/* Search */}
      <div className="relative flex-1 min-w-0">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search descriptions..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-lex-accent focus:border-lex-accent"
        />
      </div>

      {/* Date from */}
      <input
        type="date"
        value={currentFilters.date_from || ''}
        onChange={handleDateFrom}
        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        title="From date"
      />

      {/* Date to */}
      <input
        type="date"
        value={currentFilters.date_to || ''}
        onChange={handleDateTo}
        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
        title="To date"
      />

      {/* Status filter */}
      <select
        value={statusValue}
        onChange={handleStatusChange}
        className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
      >
        {activeTab === 'invoices' ? (
          <>
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="in_review">In Review</option>
            <option value="approved">Approved</option>
            <option value="revision">Revision</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </>
        ) : (
          BILLING_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))
        )}
      </select>

      {/* Create button */}
      {activeTab === 'invoices' ? (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <span className="flex items-center gap-1.5 text-sm font-medium whitespace-nowrap">
              <PlusIcon className="h-4 w-4" />
              New Invoice
              <ChevronDownIcon className="h-3 w-3 opacity-70" />
            </span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate('/billing/create-invoice')}>
              From Unbilled Items
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => store.openDrawer('bill', 'create')}>
              Custom Invoice
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <button
          onClick={handleCreate}
          className="flex items-center gap-1.5 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-lg hover:bg-lex-accent-hover transition-colors text-sm font-medium whitespace-nowrap"
        >
          <PlusIcon className="h-4 w-4" />
          {activeTab === 'time-entries' ? 'New Time Entry' : 'New Expense'}
        </button>
      )}
    </div>
  );
};

export default BillingControlsBar;
